import React, { useState, useEffect } from "react";
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { db } from '../lib/firebase.js';
import { collection, query, where, getDocs } from 'firebase/firestore';
import StoryWizard from "../components/StoryWizard/StoryWizard.js";
import { useSubscription } from '../contexts/SubscriptionContext.js';
import { AutoStories as AutoStoriesIcon } from '@mui/icons-material';

function WizardPage({ characters, addCharacter }) {
  const { user } = useUser();
  const { tier: userTier, limits, canUseAudio, isAdmin } = useSubscription();
  const [storiesThisMonth, setStoriesThisMonth] = useState(0);
  const [countLoading, setCountLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) {
      setCountLoading(false);
      return;
    }
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    const q = query(
      collection(db, 'stories'),
      where('user_id', '==', user.id)
    );
    getDocs(q)
      .then(snap => {
        const stories = snap.docs.map(doc => doc.data());
        setStoriesThisMonth(stories.filter(story => String(story.created_at || '') >= monthStart).length);
      })
      .catch(() => setStoriesThisMonth(0))
      .finally(() => setCountLoading(false));
  }, [user?.id]);

  const storyLimit = limits?.storiesPerMonth ?? 2;
  const atLimit = !isAdmin && storyLimit !== Infinity && storiesThisMonth >= storyLimit;

  if (countLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (atLimit) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', height: '60vh', gap: 2, px: 3 }}>
        <AutoStoriesIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
        <Typography variant="h5" fontWeight={600}>
          You've reached your story limit
        </Typography>
        <Typography color="text.secondary" sx={{ maxWidth: 400 }}>
          You've created {storiesThisMonth} of {storyLimit} stories this month on the <strong>{userTier || 'free'}</strong> plan.
          Upgrade to keep creating.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button variant="contained" component={RouterLink} to="/pricing">
            View Plans
          </Button>
          <Button variant="outlined" component={RouterLink} to="/library">
            My Library
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 2,
      overflow: 'auto'
    }}>
      <StoryWizard
        characters={characters}
        addCharacter={addCharacter}
        userTier={userTier}
        canUseAudio={canUseAudio}
        user={user}
        isAdmin={isAdmin}
      />
    </Box>
  );
}

export default WizardPage;
